import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Smile, ShieldCheck, Heart, Calendar, Check } from 'lucide-react';
const ACCENT = '#3B4A4F'; // tealslate — matches the header/footer
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const stagger = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function About() {
  return <div className="w-full min-h-screen bg-[#F9F9F9]" data-id="element-1716" data-mp="2c8c2ley4a37f">
      {/* HERO */}
      <section className="relative min-h-[426px] md:h-[426px] flex items-center px-6 pt-[59px] md:px-10 bg-cover bg-center bg-no-repeat" style={{
      backgroundImage: "url('https://cdn.magicpatterns.com/uploads/wYTUiK2SU4DWFQWfeVY3zi/file_00000000c0ac71f69ffd0e1784397927.png')"
    }} data-id="element-1717" data-mp="278zekqvieq39">
        {/* Soft overlay to keep text legible */}
        <div className="absolute inset-0 bg-[#F9F9F9]/55 pointer-events-none" data-id="element-1718" data-mp="39ta1rh1jzuv0" />
        <div className="relative max-w-7xl mx-auto" data-id="element-1719" data-mp="39vd2av0c16dz">
          <div className="grid grid-cols-1 items-center" data-id="element-1720" data-mp="2v5ypte1gm8ud">
            {/* Left: Copy */}
            <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-xl pt-2 md:pt-0" data-id="element-1721" data-mp="304s4h9shg5w4">
              <motion.p variants={fadeUp} className="text-[10px] font-bold uppercase tracking-[0.3em] mb-3" style={{
              color: ACCENT
            }} data-id="element-1722" data-mp="3ssxofubppquw">
                About Us
              </motion.p>

              <motion.h1 variants={fadeUp} className="font-serif text-[#1F1F1F] leading-[1.05] mb-1 text-3xl md:text-4xl lg:text-5xl" data-id="element-1723" data-mp="23dpvafp7jqkf">
                Rooted in Science.
              </motion.h1>
              <motion.h1 variants={fadeUp} className="font-serif italic leading-[1.05] mb-4 text-3xl md:text-4xl lg:text-5xl" style={{
              color: '#3B4A4F'
            }} data-id="element-1724" data-mp="dv14h660wsl8">
                Focused on You.
              </motion.h1>

              <motion.div variants={fadeUp} className="w-10 h-px bg-[#1F1F1F]/30 mb-4" data-id="element-1725" data-mp="3vcas9jvvhq2a" />

              <motion.p variants={fadeUp} className="text-sm text-[#3A3A33] leading-relaxed mb-2" data-id="element-1726" data-mp="3mc5p5lxppdtx">
                At Structured Nutrition, we believe real results come from
                personalized care, evidence-based strategies, and a coach who
                actually listens. Every plan starts with you, your goals, your
                routine, your medication, your life.
              </motion.p>
              <motion.p variants={fadeUp} className="text-sm text-[#3A3A33] leading-relaxed" data-id="element-1727" data-mp="1an4p45u87wf6">
                Our mission is simple: help you build healthier habits, improve
                your well-being, and feel your best on GLP-1s, and long after
                you taper off.
              </motion.p>

              {/* CTAs */}
              <motion.div variants={fadeUp} className="mt-5 flex flex-col sm:flex-row gap-3" data-id="element-1728" data-mp="1wtz0tqrs9qb3">
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-tealslate text-white text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-[#2A3A3F] transition-colors" data-id="element-1729" data-mp="106uadh76c0ir">
                  Book a Consultation <ArrowRight className="w-4 h-4" data-id="element-1730" data-mp="1e5tz0ouosfex" />
                </Link>
                <Link to="/services" className="inline-flex items-center justify-center px-6 py-3 border border-[#1F1F1F] text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-[#1F1F1F] hover:text-white transition-colors text-[#FFFFFF]" data-id="element-1731" data-mp="2kjtda786xcut">
                  Explore Services
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="bg-white border-y border-[#E5E5E5] py-8 md:py-10 px-6" data-id="element-1732" data-mp="bd83hn50fzi5">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-y-8 md:gap-y-0" data-id="element-1733" data-mp="6c23r5cxnbz4">
          {[{
          icon: Smile,
          stat: '500+',
          label: 'Clients Helped'
        }, {
          icon: ShieldCheck,
          stat: 'Evidence-Based',
          label: 'Nutrition Guidance'
        }, {
          icon: Heart,
          stat: '100%',
          label: 'Personalized Care'
        }, {
          icon: Calendar,
          stat: 'Years of Experience',
          label: 'You Can Trust'
        }].map((item, i) => <div key={i} className={`flex items-center justify-center gap-4 px-4 ${i > 0 ? 'md:border-l md:border-[#E5E5E5]' : ''}`} data-id="element-1734" data-mp="1in1ac0ltv33h" data-mpi={i}>
              <item.icon className="w-9 h-9 text-[#3B4A4F] shrink-0" strokeWidth={1.3} data-id="element-1735" data-mp="18pdcv2wu7hhm" />
              <div className="text-left" data-id="element-1736" data-mp="1dn6jty5ysfiy">
                <p className="text-base md:text-lg font-serif text-[#1F1F1F] leading-tight" data-id="element-1737" data-mp="1dq12pl9bxsa9">
                  {item.stat}
                </p>
                <p className="text-xs md:text-sm text-[#5A5A50] leading-tight mt-0.5" data-id="element-1738" data-mp="14nrret68luvt">
                  {item.label}
                </p>
              </div>
            </div>)}
        </div>
      </section>

      {/* OUR STORY */}
      <section className="bg-[#F9F9F9]" data-id="element-1739" data-mp="uxzu3r6r6nnn">
        <div className="grid grid-cols-1 lg:grid-cols-[43%_57%] items-stretch lg:h-[375px]" data-id="element-1740" data-mp="gy75c1esehib">
          {/* Left: Client quote (gray box) */}
          <motion.div initial={{
          opacity: 0,
          x: -24
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.8,
          ease: [0.2, 0.65, 0.3, 0.9]
        }} className="w-full h-full min-h-[320px] bg-[#E8E8E5] flex items-center justify-center px-8 md:px-12" data-id="element-1741" data-mp="kz0vz2jm6aew">
            <figure className="max-w-md text-center" data-id="element-1742" data-mp="36iz98m58alfp">
              <span className="block font-serif text-5xl leading-none text-[#9A9A8E] mb-3" aria-hidden="true" data-id="element-1743" data-mp="31n0jau494vd6">
                &ldquo;
              </span>
              <blockquote className="font-serif italic text-lg md:text-xl text-[#1F1F1F] leading-snug mb-5" data-id="element-1744" data-mp="2sk95wortfpiw">
                Working with Structured Nutrition changed everything. I finally
                understood how to eat with my medication instead of fighting it,
                and the results have lasted long after I tapered off.
              </blockquote>
              <figcaption className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5A5A50]" data-id="element-1745" data-mp="pewfqqgu7x4">
                Client
              </figcaption>
            </figure>
          </motion.div>

          {/* Right: Copy */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.3
        }} variants={stagger} className="max-w-3xl px-6 md:px-12 mx-auto lg:mx-0 py-12 lg:py-0 self-center" data-id="element-1746" data-mp="agjbsrk1zush">
            <motion.p variants={fadeUp} className="text-[11px] font-bold uppercase tracking-[0.3em] mb-6" style={{
            color: ACCENT
          }} data-id="element-1747" data-mp="ogkd2848d48h">
              Our Story
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-serif text-[#1F1F1F] leading-[1.15] text-2xl md:text-3xl mb-5" data-id="element-1748" data-mp="2v571jw6wotkg">
              A passion for people. A commitment to results.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-sm text-[#3A3A33] leading-relaxed mb-4" data-id="element-1749" data-mp="ww176knfwac0">
              Structured Nutrition was founded with one goal in mind: to make
              high-quality GLP-1 nutrition care accessible, personalized, and
              effective. We've helped hundreds of clients overcome challenges,
              build confidence, and create sustainable habits that last well
              beyond the medication.
            </motion.p>
            <motion.p variants={fadeUp} className="text-sm text-[#1F1F1F] font-bold leading-relaxed mb-7" data-id="element-1750" data-mp="rv3q6be61mc3">
              Your health journey is unique — your plan should be too.
            </motion.p>
            <motion.div variants={fadeUp} data-id="element-1751" data-mp="1yds9szlyfwph">
              <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-4 text-[11px] font-bold uppercase tracking-[0.2em] text-white transition-colors" style={{
              backgroundColor: '#3B4A4F'
            }} data-id="element-1752" data-mp="1yd0nkfe8zbbv">
                Ready To Learn More? <ArrowRight className="w-4 h-4" data-id="element-1753" data-mp="37cjyzywwpmo7" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* TEAM / APPROACH */}
      <section className="bg-white py-16 md:py-20 px-6 md:px-10" data-id="element-1754" data-mp="3ua63al5y0br">
        <div className="max-w-7xl mx-auto" data-id="element-1755" data-mp="7u0sl23npuah">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-center" data-id="element-1756" data-mp="7x6g4cwn0o43">
            {/* Left: Headshot + signature */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.3
          }} transition={{
            duration: 0.7,
            ease: [0.2, 0.65, 0.3, 0.9]
          }} className="md:col-span-3 flex flex-col items-center md:items-start" data-id="element-1757" data-mp="3v7ml3z4btc0z">
              <div className="w-32 h-32 md:w-36 md:h-36 rounded-full overflow-hidden bg-[#E8E4D8] mb-5" data-id="element-1758" data-mp="1u8yk6g1g0ke9">
                <img src="https://cdn.magicpatterns.com/uploads/fBWp7VcHydeQL4QeV4Xzp2/pasted-image.png" alt="Jack Achenbach — Structured Nutrition" className="w-full h-full object-cover" style={{
                objectPosition: '42% center'
              }} data-id="element-1759" data-mp="1y9qs95ykwj8y" />
              </div>
              <p className="text-3xl text-[#3A3A33] mb-1" style={{
              fontFamily: '"Dancing Script", "Brush Script MT", cursive'
            }} data-id="element-1760" data-mp="1flvkq3tk81oa">
                With care,
              </p>
              <p className="text-sm font-bold text-[#1F1F1F] uppercase tracking-wide" data-id="element-1761" data-mp="mycb9ewm3fh4">
                The Structured Nutrition Team
              </p>
            </motion.div>

            {/* Middle: Copy */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.3
          }} variants={stagger} className="md:col-span-5" data-id="element-1762" data-mp="vysd6jxpiejv">
              <motion.p variants={fadeUp} className="text-[11px] font-bold uppercase tracking-[0.3em] mb-4 text-[#1F1F1F]" data-id="element-1763" data-mp="15uvdrlcwu54p">
                Our Team
              </motion.p>
              <motion.h2 variants={fadeUp} className="font-serif text-[#1F1F1F] leading-[1.15] text-2xl md:text-3xl mb-5" data-id="element-1764" data-mp="1ao6npvd0r20u">
                Built on experience. Driven by purpose.
              </motion.h2>
              <motion.p variants={fadeUp} className="text-sm text-[#3A3A33] leading-relaxed mb-4" data-id="element-1765" data-mp="1jq7k6q0fhk1g">
                Structured Nutrition was founded by a team of nutrition
                professionals who saw a need for a more sustainable,
                personalized, and realistic approach to healthy living.
              </motion.p>
              <motion.p variants={fadeUp} className="text-sm text-[#3A3A33] leading-relaxed" data-id="element-1766" data-mp="v3esdx1hrxpj">
                We're here to cut through the noise and help you build a
                healthier relationship with food, one step at a time.
              </motion.p>
            </motion.div>

            {/* Right: Check list */}
            <motion.ul initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.3
          }} variants={stagger} className="md:col-span-4 space-y-6" data-id="element-1767" data-mp="1e1zefaoj0cwb">
              {['Registered & Qualified Nutrition Professionals', 'Ongoing Education & Training', 'Focused on Real Results'].map((item, i) => <motion.li key={i} variants={fadeUp} className="flex items-center gap-4" data-id="element-1768" data-mp="2yj0af94pif65" data-mpi={i}>
                  <span className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{
                backgroundColor: '#3B4A4F'
              }} data-id="element-1769" data-mp="2uftvze7fxhol">
                    <Check className="w-4 h-4 text-white" strokeWidth={3} data-id="element-1770" data-mp="3i6pt8quo1538" />
                  </span>
                  <span className="text-sm md:text-base font-bold text-[#1F1F1F] leading-snug" data-id="element-1771" data-mp="3d97h6rlcq9r0">
                    {item}
                  </span>
                </motion.li>)}
            </motion.ul>
          </div>
        </div>
      </section>
    </div>;
}